import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import {
  MP_LABEL,
  accountsUrl,
  asList,
  mergeSelectedOptions,
  mpWarehousesUrl,
  sameIdList,
} from './msAssortmentApi.mjs';

function emptyStoreSet() {
  return { gp_store_ids: [], production_store_id: null, materials_store_id: null };
}

function ShopStoreFields({ stores, storeById, value, onChange, labels }) {
  const gpIds = value?.gp_store_ids || [];
  const productionId = value?.production_store_id || null;
  const materialsId = value?.materials_store_id || null;
  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        options={stores}
        getOptionLabel={(o) => o.name}
        value={stores.filter((s) => gpIds.includes(s.id))}
        onChange={(_, val) => onChange({ gp_store_ids: val.map((v) => v.id) })}
        renderInput={(params) => <TextField {...params} label={labels.gp} />}
      />
      <Autocomplete
        options={stores}
        getOptionLabel={(o) => o.name}
        value={storeById.get(productionId) || null}
        onChange={(_, val) => onChange({ production_store_id: val?.id || null })}
        renderInput={(params) => <TextField {...params} label={labels.shop} />}
      />
      <Autocomplete
        options={stores}
        getOptionLabel={(o) => o.name}
        value={storeById.get(materialsId) || null}
        onChange={(_, val) => onChange({ materials_store_id: val?.id || null })}
        renderInput={(params) => <TextField {...params} label={labels.materials} />}
      />
      {productionId && !gpIds.includes(productionId) && (
        <Alert severity="warning">{labels.gpWarning}</Alert>
      )}
    </Stack>
  );
}

const STORE_LIST_KEYS = ['ms_store_ids', 'mp_warehouse_ids'];

function emptyDraft() {
  return { name: '', ms_store_ids: [], mp_warehouse_ids: [] };
}

function draftFromBinding(binding) {
  const draft = { name: binding?.name || '' };
  for (const key of STORE_LIST_KEYS) {
    draft[key] = Array.isArray(binding?.[key]) ? binding[key].map(String) : [];
  }
  return draft;
}

function isDraftDirty(binding, draft) {
  if (!binding || !draft) return false;
  return (draft.name || '') !== (binding.name || '')
    || STORE_LIST_KEYS.some((key) => !sameIdList(draft[key], binding[key] || []));
}

function MsStoreSelect({ stores, value, onChange, label, helperText }) {
  const options = mergeSelectedOptions(stores, value);
  return (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
      value={options.filter((s) => value.includes(String(s.id)))}
      onChange={(_, val) => onChange(val.map((v) => String(v.id)))}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
    />
  );
}

export default function MsStoresDialog({
  open,
  onClose,
  settings,
  setSettings,
  stores,
  storeById,
  runs,
  savingSettings,
  onSaveReplenishment,
  notify,
}) {
  const [tab, setTab] = useState('replenishment');
  const [accounts, setAccounts] = useState([]);
  const [accountKey, setAccountKey] = useState('');
  const [bindings, setBindings] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [mpWarehouses, setMpWarehouses] = useState([]);
  const [newBindingName, setNewBindingName] = useState('');
  const [loadingMp, setLoadingMp] = useState(false);
  const [savingBindings, setSavingBindings] = useState(false);
  const [busyBindingId, setBusyBindingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const selectedAccount = useMemo(
    () => accounts.find((acc) => `${acc.marketplace}:${acc.id}` === accountKey) || null,
    [accounts, accountKey],
  );

  const loadAccounts = useCallback(async () => {
    try {
      const [wb, oz, ya] = await Promise.all([
        axios.get(accountsUrl('wb'), { silent: true }).catch(() => ({ data: [] })),
        axios.get(accountsUrl('ozon'), { silent: true }).catch(() => ({ data: [] })),
        axios.get(accountsUrl('yandex'), { silent: true }).catch(() => ({ data: [] })),
      ]);
      const list = [
        ...asList(wb.data).map((acc) => ({ ...acc, marketplace: 'wb' })),
        ...asList(oz.data).map((acc) => ({ ...acc, marketplace: 'ozon' })),
        ...asList(ya.data).map((acc) => ({ ...acc, marketplace: 'yandex' })),
      ];
      setAccounts(list);
    } catch (_) {
      setAccounts([]);
    }
  }, []);

  const loadCabinetData = useCallback(async (account) => {
    if (!account?.id || !account.marketplace) {
      setBindings([]);
      setDrafts({});
      setMpWarehouses([]);
      return;
    }
    setLoadingMp(true);
    try {
      const [bindingsRes, warehousesRes] = await Promise.all([
        axios.get('/ms-assortment/bindings', {
          params: { marketplace: account.marketplace, account_id: account.id },
        }),
        axios.get(mpWarehousesUrl(), {
          params: { marketplace: account.marketplace, account_id: account.id },
          silent: true,
        }).catch(() => ({ data: [] })),
      ]);
      const list = asList(bindingsRes.data);
      setBindings(list);
      const nextDrafts = {};
      for (const binding of list) nextDrafts[binding.id] = draftFromBinding(binding);
      setDrafts(nextDrafts);
      setMpWarehouses(asList(warehousesRes.data).map((w) => ({
        id: String(w.id),
        name: w.name || String(w.id),
      })).filter((w) => w.id));
    } catch (error) {
      setBindings([]);
      setDrafts({});
      setMpWarehouses([]);
      notify.error(error?.response?.data?.message || 'Не удалось загрузить связки складов');
    } finally {
      setLoadingMp(false);
    }
  }, [notify]);

  useEffect(() => {
    if (!open) return;
    loadAccounts();
  }, [open, loadAccounts]);

  useEffect(() => {
    if (!open || tab !== 'mp') return;
    loadCabinetData(selectedAccount);
  }, [open, tab, selectedAccount, loadCabinetData]);

  const draftFor = (binding) => drafts[binding.id] || draftFromBinding(binding);

  const setDraft = (bindingId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [bindingId]: { ...(prev[bindingId] || emptyDraft()), ...patch },
    }));
  };

  const reservedMpWarehouses = (excludeBindingId) => {
    const reserved = new Set();
    for (const binding of bindings) {
      if (excludeBindingId != null && Number(binding.id) === Number(excludeBindingId)) continue;
      const ids = draftFor(binding).mp_warehouse_ids;
      for (const id of ids) reserved.add(String(id));
    }
    return reserved;
  };

  const dirtyBindings = bindings.filter((binding) => isDraftDirty(binding, draftFor(binding)));

  const saveBindingDrafts = async () => {
    if (!selectedAccount) {
      notify.warning('Выберите кабинет');
      return;
    }
    if (!dirtyBindings.length) {
      notify.info('Нет изменений в связках');
      return;
    }
    setSavingBindings(true);
    try {
      for (const binding of dirtyBindings) {
        const draft = draftFor(binding);
        const payload = { name: draft.name };
        for (const key of STORE_LIST_KEYS) payload[key] = (draft[key] || []).map(String);
        await axios.put(`/ms-assortment/bindings/${binding.id}`, payload);
      }
      notify.success(dirtyBindings.length === 1 ? 'Связка сохранена' : `Сохранены связки: ${dirtyBindings.length}`);
      await loadCabinetData(selectedAccount);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить связки');
    } finally {
      setSavingBindings(false);
    }
  };

  const createBinding = async () => {
    if (!selectedAccount) return;
    const name = newBindingName.trim() || 'Без имени';
    setBusyBindingId('new');
    try {
      await axios.post('/ms-assortment/bindings', {
        marketplace: selectedAccount.marketplace,
        account_id: Number(selectedAccount.id),
        name,
        ms_store_ids: [],
        mp_warehouse_ids: [],
      });
      setNewBindingName('');
      notify.success('Связка создана');
      await loadCabinetData(selectedAccount);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось создать связку');
    } finally {
      setBusyBindingId(null);
    }
  };

  const deleteBinding = async (bindingId) => {
    setBusyBindingId(bindingId);
    try {
      await axios.delete(`/ms-assortment/bindings/${bindingId}`);
      notify.success('Связка удалена');
      await loadCabinetData(selectedAccount);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось удалить связку');
    } finally {
      setBusyBindingId(null);
    }
  };

  const syncBinding = async (binding) => {
    if (isDraftDirty(binding, draftFor(binding))) {
      notify.warning('Сначала сохраните изменения связки');
      return;
    }
    setBusyBindingId(binding.id);
    try {
      const { data } = await axios.post(`/ms-assortment/bindings/${binding.id}/stocks/sync`);
      notify.success(`Синхронизация: ${data?.items ?? data?.skusSent ?? 0} позиций`);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось синхронизировать связку');
    } finally {
      setBusyBindingId(null);
    }
  };

  const syncAllBindings = async () => {
    if (!selectedAccount) return;
    if (dirtyBindings.length) {
      notify.warning('Сначала сохраните изменения связок');
      return;
    }
    setSyncingAll(true);
    try {
      const { data } = await axios.post('/ms-assortment/bindings/stocks/sync', {
        marketplace: selectedAccount.marketplace,
        account_id: Number(selectedAccount.id),
      });
      notify.success(`Синхронизация всех связок: ${data?.items ?? data?.skusSent ?? 0} позиций`);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось синхронизировать кабинет');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSave = () => {
    if (tab === 'replenishment') onSaveReplenishment();
    else saveBindingDrafts();
  };

  const saving = tab === 'replenishment' ? savingSettings : savingBindings;
  const busy = saving || busyBindingId != null || syncingAll || loadingMp;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Склады</DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="replenishment" label="Автопополнение" />
          <Tab value="mp" label="Связь с МП" />
        </Tabs>

        {tab === 'replenishment' && settings && (
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={!!settings.enabled} onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))} />}
              label="Включить автопополнение по расписанию (07:30 и 12:30 МСК, пока выключено)"
            />
            <Typography variant="subtitle2">Производство</Typography>
            <ShopStoreFields
              stores={stores}
              storeById={storeById}
              value={settings}
              onChange={(patch) => setSettings((s) => ({ ...s, ...patch }))}
              labels={{
                gp: 'Склады ГП (проверка «доступно»)',
                shop: 'Склад производства (выпуск и списание)',
                materials: 'Склад материалов (источник перемещения)',
                gpWarning: 'Добавьте склад производства в список складов ГП, чтобы учитывать ожидание заданий.',
              }}
            />
            <Divider />
            <Typography variant="subtitle2">Швейка</Typography>
            <ShopStoreFields
              stores={stores}
              storeById={storeById}
              value={settings.sewing || emptyStoreSet()}
              onChange={(patch) => setSettings((s) => ({
                ...s,
                sewing: { ...(s.sewing || emptyStoreSet()), ...patch },
              }))}
              labels={{
                gp: 'Склады ГП швейки (проверка «доступно»)',
                shop: 'Склад швейки (выпуск и списание)',
                materials: 'Склад ткани / материалов швейки (источник перемещения)',
                gpWarning: 'Добавьте склад швейки в список складов ГП швейки, чтобы учитывать ожидание заданий.',
              }}
            />
            {runs.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Последние прогоны</Typography>
                {runs.map((run) => (
                  <Typography key={run.id} variant="caption" display="block" color="text.secondary">
                    #{run.id} · {run.trigger} · {run.status}
                    {run.dry_run ? ' · план' : ''}
                    {run.error_message ? ` · ${run.error_message}` : ''}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}

        {tab === 'mp' && (
          <Stack spacing={2}>
            <Alert severity="info">
              Склад МП уникален между связками одного кабинета. Остаток — сумма МойСклад по складам МС связки
              плюс сколько ещё можно собрать по техкартам из чехлов, полуфабрикатов и ткани (их склады —
              общие, на странице «Привязка чехлов»); если задан принуд — уходит он. Выключенная связь на МП не отправляется.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                <InputLabel>Кабинет</InputLabel>
                <Select
                  label="Кабинет"
                  value={accountKey}
                  onChange={(e) => setAccountKey(e.target.value)}
                >
                  {accounts.map((acc) => (
                    <MenuItem key={`${acc.marketplace}:${acc.id}`} value={`${acc.marketplace}:${acc.id}`}>
                      {acc.name || acc.id} · {MP_LABEL[acc.marketplace] || acc.marketplace}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                disabled={!selectedAccount || !bindings.length || busy}
                onClick={syncAllBindings}
              >
                {syncingAll ? 'Синхронизируем…' : 'Синхронизировать все связки кабинета'}
              </Button>
            </Stack>

            {!selectedAccount && (
              <Typography variant="body2" color="text.secondary">Выберите кабинет WB, Ozon или Yandex.</Typography>
            )}
            {selectedAccount && !loadingMp && bindings.length === 0 && (
              <Typography variant="body2" color="text.secondary">Связок ещё нет — добавьте первую ниже.</Typography>
            )}

            {bindings.map((binding) => {
              const draft = draftFor(binding);
              const reserved = reservedMpWarehouses(binding.id);
              const mpOptions = mergeSelectedOptions(mpWarehouses, draft.mp_warehouse_ids, 'Склад');
              const dirty = isDraftDirty(binding, draft);
              return (
                <Paper key={binding.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label="Имя связки"
                        value={draft.name}
                        onChange={(e) => setDraft(binding.id, { name: e.target.value })}
                        sx={{ minWidth: 180, flex: 1 }}
                      />
                      {dirty && <Typography variant="caption" color="warning.main">не сохранено</Typography>}
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() => deleteBinding(binding.id)}
                        aria-label="Удалить связку"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <MsStoreSelect
                      stores={stores}
                      value={draft.ms_store_ids}
                      onChange={(ids) => setDraft(binding.id, { ms_store_ids: ids })}
                      label="Склады МС (товар)"
                    />
                    <Autocomplete
                      multiple
                      options={mpOptions}
                      getOptionLabel={(o) => o.name}
                      isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                      getOptionDisabled={(o) => reserved.has(String(o.id)) && !draft.mp_warehouse_ids.includes(String(o.id))}
                      value={mpOptions.filter((w) => draft.mp_warehouse_ids.includes(String(w.id)))}
                      onChange={(_, val) => setDraft(binding.id, { mp_warehouse_ids: val.map((v) => String(v.id)) })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Склады МП"
                          helperText="Занятый другой связкой склад выбрать нельзя"
                        />
                      )}
                    />
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SyncIcon />}
                        disabled={busy}
                        onClick={() => syncBinding(binding)}
                      >
                        {busyBindingId === binding.id ? 'Синхронизируем…' : 'Синхронизировать'}
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}

            {selectedAccount && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  label="Имя новой связки"
                  value={newBindingName}
                  onChange={(e) => setNewBindingName(e.target.value)}
                  sx={{ minWidth: 220, flex: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={busy}
                  onClick={createBinding}
                >
                  Добавить связку
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (tab === 'mp' && !selectedAccount)}
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
